import { useCallback } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';

import { useBraze } from '@order/Braze';
import { SWEETPASS_BRAZE_CUSTOM_EVENTS } from '@order/constants';
import type { TierName } from '@order/graphql';
import { useCancelSubscriptionAtCycleEndMutation } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import type { AccountSweetpassCancellationSection } from '../useSweetpassAccountContentfulData';
import {
  ACCOUNT_SWEETPASS_CANCELLATION_SECTION,
  useSweetpassAccountContentfulData,
} from '../useSweetpassAccountContentfulData';

export const useCancelSweetpassMembership = (
  props: CancelSweetpassMembershipProps,
) => {
  const { subscriptionId, nextBillingDate, tierName, refetchMembership } =
    props;

  const { content } = useSweetpassAccountContentfulData();
  const cancellationSection = content?.find?.(
    (section) => section.name === ACCOUNT_SWEETPASS_CANCELLATION_SECTION,
  ) as AccountSweetpassCancellationSection;
  const {
    cancellationDataGenericErrorMessage,
    cancellationDataGenericSuccessMessage,
  } = cancellationSection ?? {};

  const { formatDate } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { logCustomEvent } = useBraze();

  const [, cancelSubscriptionAtCycleEndMutation] =
    useCancelSubscriptionAtCycleEndMutation();

  const logCancellationCustomBrazeEvent = useCallback(() => {
    if (tierName === 'SWEETPASS') {
      logCustomEvent(
        SWEETPASS_BRAZE_CUSTOM_EVENTS.UserCancelledSweetpassSubscription,
      );
    }

    if (tierName === 'SWEETPASS_PLUS') {
      logCustomEvent(
        SWEETPASS_BRAZE_CUSTOM_EVENTS.UserCancelledSweetpassPlusSubscription,
      );
    }
  }, [logCustomEvent, tierName]);

  const cancelSubscriptionAtCycleEnd = useCallback(async () => {
    if (!subscriptionId) return;

    const { data: cancelSubscriptionData } =
      await cancelSubscriptionAtCycleEndMutation({
        subscriptionId,
      });

    const hasFailedToCancel =
      cancelSubscriptionData?.cancelSubscriptionAtCycleEnd?.__typename !==
      'CancelSubscriptionAtCycleEndSuccessResponse';

    if (hasFailedToCancel) {
      addNoticeBanner({
        text: cancellationDataGenericErrorMessage,
        palette: 'caution',
      });

      return;
    }

    addNoticeBanner({
      text: cancellationDataGenericSuccessMessage.replace(
        '{nextBillingDate}',
        nextBillingDate ? formatDate(nextBillingDate) : '',
      ),
      palette: 'success',
    });

    logCancellationCustomBrazeEvent();

    refetchMembership();
  }, [
    subscriptionId,
    cancelSubscriptionAtCycleEndMutation,
    addNoticeBanner,
    cancellationDataGenericSuccessMessage,
    nextBillingDate,
    formatDate,
    logCancellationCustomBrazeEvent,
    refetchMembership,
    cancellationDataGenericErrorMessage,
  ]);

  return { cancelSubscriptionAtCycleEnd };
};

type CancelSweetpassMembershipProps = {
  readonly tierName: TierName | undefined;
  readonly subscriptionId?: string;
  readonly nextBillingDate?: string;
  readonly refetchMembership: () => void;
};
